// src/pages/Vendor/Apply/components/Step1Business.tsx
import React, { useState, useRef, useEffect } from 'react';
import { Store, Mail, Globe, FileText, AlertCircle, Info } from 'lucide-react';
import { PhoneInput, validatePhoneNumber } from '../../../../components/common/PhoneInput';
import BusinessAddressForm from '../../../../components/common/BusinessAddressForm';
import { generateIndividualStoreName } from '../../../../utils/stringUtils';
import { useUser } from '../../../../hooks/useUser';
import Select from '../../../../components/common/Select';

const VENDOR_TYPE_OPTIONS = [
    { 
        value: 'individual', 
        label: 'Individual',
        description: 'Sell under your personal name. No registration required.'
    },
    { 
        value: 'sole_proprietor', 
        label: 'Sole Proprietor',
        description: 'Registered sole proprietorship business.'
    },
    { 
        value: 'llc', 
        label: 'LLC',
        description: 'Limited Liability Company.'
    },
    { 
        value: 'corporation', 
        label: 'Corporation',
        description: 'Incorporated business entity.'
    },
    { 
        value: 'partnership', 
        label: 'Partnership',
        description: 'Two or more owners.'
    },
    { 
        value: 'non_profit', 
        label: 'Non-Profit Organization',
        description: 'Charitable or non-profit entity.'
    },
];

interface Step1BusinessProps {
    data: any;
    onNext: (data: any) => void;
}

const Step1Business: React.FC<Step1BusinessProps> = ({ data, onNext }) => {
    const { user } = useUser();
    const [form, setForm] = useState({
        business_name: data.business_name || '',
        business_email: data.business_email || '',
        business_phone: data.business_phone || '',
        vendor_type: data.vendor_type || '',
        website: data.website || '',
        description: data.description || '',
        registration_number: data.registration_number || '',
        tax_id: data.tax_id || '',
        registration_file: data.registration_file || null,
        address_proof: data.address_proof || null,
    });

    const [addressData, setAddressData] = useState({
        house_number: data.business_address?.house_number || '',
        address_line_1: data.business_address?.address_line_1 || '',
        city_id: data.business_address?.city_id || '',
        city_name: data.business_address?.city_name || '',
        address_line_2: data.business_address?.address_line_2 || '',
        captured_location: data.business_address?.captured_location || null,
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [addressErrors, setAddressErrors] = useState<{
        address_line_1?: string;
        city?: string;
        house_number?: string;
        country?: string;
    }>({});
    const [touched, setTouched] = useState<Record<string, boolean>>({});

    // Refs
    const businessNameRef = useRef<HTMLDivElement>(null);
    const emailRef = useRef<HTMLDivElement>(null);
    const phoneRef = useRef<HTMLDivElement>(null);
    const addressRef = useRef<HTMLDivElement>(null);
    const businessTypeRef = useRef<HTMLDivElement>(null);

    const validateField = (name: string, value: any): string => {
        switch (name) {
            case 'business_name':
                if (!value?.trim()) return 'Business name is required';
                if (value.length < 2) return 'Business name must be at least 2 characters';
                if (value.length > 100) return 'Business name must be less than 100 characters';
                return '';
            case 'business_email':
                if (!value?.trim()) return 'Business email is required';
                if (!/\S+@\S+\.\S+/.test(value)) return 'Please enter a valid email address';
                return '';
            case 'business_phone':
                return validatePhoneNumber(value, true);
            case 'business_address':
                if (typeof value === 'object') {
                    if (!value?.address_line_1) return 'Street address is required';
                    if (!value?.city_id) return 'City is required';
                    return '';
                }
                // Fallback for string
                if (!value?.trim()) return 'Business address is required';
                return '';
            case 'vendor_type':
                if (!value) return 'Please select a vendor type';
                return '';
            case 'website':
                if (value) {
                    // Try with https:// for validation
                    const testUrl = value.match(/^https?:\/\//) ? value : `https://${value}`;
                    if (!/^https?:\/\/.+/.test(testUrl)) {
                        return 'Please enter a valid URL';
                    }
                }
                return '';
            default:
                return '';
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};
        const newAddressErrors: { address_line_1?: string; city?: string; house_number?: string } = {};

        // Validate address fields
        if (!addressData.address_line_1) {
            newAddressErrors.address_line_1 = 'Street address is required';
        }
        // City validation - with autofill guidance
        if (!addressData.city_id) {
            if (addressData.city_name) {
                // User typed a city name but didn't select from dropdown
                newAddressErrors.city = 'Please select a city from the list above, or tap to choose';
            } else {
                newAddressErrors.city = 'Please select a city';
            }
        }


        // Validate other fields
        const fields = ['business_name', 'business_email', 'business_phone', 'vendor_type'];
        fields.forEach(field => {
            const error = validateField(field, form[field as keyof typeof form]);
            if (error) newErrors[field] = error;
        });

        // Website validation
        const websiteError = validateField('website', form.website);
        if (websiteError) newErrors.website = websiteError;

        setErrors(newErrors);
        setAddressErrors(newAddressErrors);
        return Object.keys(newErrors).length === 0 && Object.keys(newAddressErrors).length === 0;
    };

    const scrollToError = () => {
        const errorFields = Object.keys(errors);
        const addressErrorFields = Object.keys(addressErrors);
        const allErrors = [...errorFields, ...addressErrorFields];

        if (allErrors.length === 0) return;

        // Check if the first error is an address error
        const firstError = allErrors[0];
        const isAddressError = addressErrorFields.includes(firstError);

        if (isAddressError) {
            // Scroll to and focus the specific address field
            const fieldMap: Record<string, string> = {
                address_line_1: 'address-address_line_1',
                city: 'address-city',
                house_number: 'address-house_number',
            };

            const inputName = fieldMap[firstError];
            if (inputName) {
                const input = document.querySelector(`[name="${inputName}"]`) as HTMLElement;
                if (input) {
                    input.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    setTimeout(() => input.focus(), 300);
                    return;
                }
            }

            // Fallback: scroll to container
            const container = document.getElementById('business-address-container');
            if (container) {
                container.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        // Handle regular field errors
        const refMap: Record<string, React.RefObject<HTMLDivElement | null>> = {
            business_name: businessNameRef,
            business_email: emailRef,
            business_phone: phoneRef,
            vendor_type: businessTypeRef,
        };

        const ref = refMap[firstError];
        if (ref?.current) {
            ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Find and focus the input inside the ref
            const input = ref.current.querySelector('input, select, textarea') as HTMLElement;
            if (input) setTimeout(() => input.focus(), 300);
        }
    };

    // const handleWebsiteChange = (value: string) => {
    //     let formattedValue = value;
    //     // If user didn't type https://, add it for display
    //     if (value && !value.match(/^https?:\/\//)) {
    //         formattedValue = `https://${value}`;
    //     }
    //     setForm({ ...form, website: formattedValue });
    // };

    const handleBlur = (field: string) => {
        setTouched({ ...touched, [field]: true });
        const error = validateField(field, form[field as keyof typeof form]);
        if (error) {
            setErrors({ ...errors, [field]: error });
        } else {
            const newErrors = { ...errors };
            delete newErrors[field];
            setErrors(newErrors);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        const allTouched: Record<string, boolean> = {};
        ['business_name', 'business_email', 'business_phone', 'business_address', 'vendor_type'].forEach(f => {
            allTouched[f] = true;
        });
        setTouched(allTouched);

        if (validateForm()) {
            onNext({
                ...form,
                business_address: addressData,
            });
        } else {
            scrollToError();
        }
    };

    // Helper to clear error for a specific field
    const clearFieldError = (field: string) => {
        if (errors[field]) {
            const newErrors = { ...errors };
            delete newErrors[field];
            setErrors(newErrors);
        }
    };

    const clearAddressError = (field: 'address_line_1' | 'city' | 'house_number' | 'country') => {
        if (addressErrors[field]) {
            const newErrors = { ...addressErrors };
            delete newErrors[field];
            setAddressErrors(newErrors);
        }
    };

    return (
        <form onSubmit={handleSubmit} noValidate>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Business Information</h2>
            <p className="text-sm text-gray-500 mb-6">Tell us about your business</p>

            <div className="space-y-4">
                {/* Vendor Type - Moved to top */}
                <div ref={businessTypeRef}>
                    <Select
                        value={form.vendor_type}
                        onChange={(value) => {
                            setForm({ ...form, vendor_type: value });
                            clearFieldError('vendor_type');
                            // Auto-generate name for individual
                            if (value === 'individual' && user) {
                                setForm(prev => ({
                                    ...prev,
                                    business_name: generateIndividualStoreName(user)
                                }));
                            }
                        }}
                        options={VENDOR_TYPE_OPTIONS}
                        label="Vendor Type"
                        placeholder="Select your vebdor type..."
                        error={errors.vendor_type}
                        required={true}
                    />
                    {form.vendor_type === 'individual' && (
                        <div className="mt-2 flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg p-2">
                            <Info className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-blue-700">
                                You'll sell under your legal name. No registration or tax ID required.
                            </p>
                        </div>
                    )}
                </div>

                {/* Business Name - Dynamic based on vendor type */}
                <div ref={businessNameRef}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Business Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <Store className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            value={form.business_name}
                            onChange={(e) => {
                                if (form.vendor_type !== 'individual') {
                                    setForm({ ...form, business_name: e.target.value });
                                    clearFieldError('business_name');
                                }
                            }}
                            onBlur={() => handleBlur('business_name')}
                            disabled={form.vendor_type === 'individual' || form.vendor_type === ''}
                            className={`w-full pl-10 pr-3 py-2.5 border rounded-lg shadow-sm text-sm transition-colors ${form.vendor_type === 'individual'
                                ? 'bg-gray-100 text-gray-600 cursor-not-allowed'
                                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                                } ${errors.business_name && touched.business_name
                                    ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500'
                                    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                                }`}
                            placeholder="e.g., Tech Haven Electronics"
                            maxLength={100}
                        />
                    </div>
                    <div className="flex justify-between items-center mt-1">
                        <p className="text-xs text-gray-500">
                            {form.vendor_type === 'individual'
                                ? 'Individual vendors must use their personal name.'
                                : 'Your registered business or trading name'
                            }
                        </p>
                        <span className="text-xs text-gray-400">{form.business_name.length}/100</span>
                    </div>
                    {errors.business_name && touched.business_name && (
                        <div className="mt-1 flex items-center text-sm text-red-600">
                            <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                            {errors.business_name}
                        </div>
                    )}
                </div>

                {/* Email & Phone */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div ref={emailRef}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Business Email <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                type="email"
                                value={form.business_email}
                                onChange={(e) => {
                                    setForm({ ...form, business_email: e.target.value });
                                    clearFieldError('business_email');
                                }}
                                onBlur={() => handleBlur('business_email')}
                                className={`w-full pl-10 pr-3 py-2.5 border rounded-lg shadow-sm text-sm transition-colors ${errors.business_email && touched.business_email
                                    ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500'
                                    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                                    }`}
                                placeholder="business@email.com"
                            />
                        </div>
                        {errors.business_email && touched.business_email && (
                            <div className="mt-1 flex items-center text-sm text-red-600">
                                <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                                {errors.business_email}
                            </div>
                        )}
                    </div>
                    <div ref={phoneRef}>
                        <PhoneInput
                            value={form.business_phone}
                            onChange={(value) => {
                                setForm({ ...form, business_phone: value || '' });
                                clearFieldError('business_phone');
                            }}
                            label="Phone Number *"
                            error={touched.business_phone ? errors.business_phone : ''}
                            name="business_phone"
                            placeholder="+1 868 123-4567"
                        />
                    </div>
                </div>

                {/* Address */}
                <div ref={addressRef} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                        Business Address <span className="text-red-500">*</span>
                    </label>
                    <BusinessAddressForm
                        value={addressData}
                        onChange={setAddressData}
                        errors={addressErrors}
                        onClearError={clearAddressError}
                    />
                </div>

                {/* Website */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Website <span className="text-xs text-gray-500">(optional)</span>
                    </label>
                    <div className="relative">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            value={form.website.replace(/^https?:\/\//, '')}
                            onChange={(e) => {
                                const value = e.target.value;
                                setForm({ ...form, website: value });
                                if (errors.website) {
                                    const newErrors = { ...errors };
                                    delete newErrors.website;
                                    setErrors(newErrors);
                                }
                            }}
                            placeholder="yourwebsite.com"
                            className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg shadow-sm text-sm focus:border-blue-500 focus:ring-blue-500 transition-colors"
                        />
                    </div>
                    {errors.website && touched.website && (
                        <div className="mt-1 flex items-center text-sm text-red-600">
                            <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                            {errors.website}
                        </div>
                    )}
                </div>

                {/* Description */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Business Description <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <FileText className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <textarea
                            value={form.description}
                            onChange={(e) => {
                                setForm({ ...form, description: e.target.value });
                                clearFieldError('description');
                            }}
                            onBlur={() => handleBlur('description')}
                            rows={3}
                            className={`w-full pl-10 pr-3 py-2.5 border rounded-lg shadow-sm text-sm transition-colors ${errors.description && touched.description
                                ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500'
                                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                                }`}
                            placeholder="Describe your business, products, and what you plan to sell..."
                            maxLength={500}
                            required
                        />
                    </div>
                    <div className="flex justify-between items-center mt-1">
                        <p className="text-xs text-gray-500">Describe your business and products</p>
                        <span className="text-xs text-gray-400">{form.description.length}/500</span>
                    </div>
                    {errors.description && touched.description && (
                        <div className="mt-1 flex items-center text-sm text-red-600">
                            <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                            {errors.description}
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end">
                <button
                    type="submit"
                    className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors shadow-sm hover:shadow"
                >
                    Continue →
                </button>
            </div>
        </form>
    );
};

export default Step1Business;